import React, { Component, createRef } from "react";
import qs from "qs";
import { MultiSelect } from 'primereact/multiselect';
import { isEmpty, get, intersection } from "lodash";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { request } from "../../utils/Request";
import { ColumnGroup } from "primereact/columngroup";
import { Col, Row } from "react-bootstrap";
import { Paginator } from 'primereact/paginator';
import { css, cx } from '@emotion/css';
import QuickCopyDropdown from "./QuickCopyDropdown";

interface ITableProps {
    url: string,
    columns: any,
    size?: string,
    moduleState?: any,
    setQuery?: any,
    name?: any,
    detailRow?: any,
    trackBy?: any,
    selectAllMode?: any,
    enableTableSearch?: any,
    getFilterRef?: any,
    getMetaData?: any,
    onRowSelection?: any,
    selectedRows?: any[],
    setRowClass?: any,
    itemSize?: number,
    setTotal?: any,
    getExtra?: any,
    virtualScroll?: boolean,
    state?: boolean,
    stickyHeader?: boolean,
    showQuickCopy?: boolean,
    quickCopyPosition?: "top" | "bottom",
    showLoader?: boolean,
    loaderLabel?: string,
    method?: "GET" | "POST",
}

class StateLessGrid extends Component<any, any> {
    dataGrid: any = null;

    static defaultProps: any = {
        size: "small",
        trackBy: "id",
        itemSize: 75,
        virtualScroll: false,
        state: false,
        stickyHeader: false,
        showLoader: false,
        loaderLabel: null,
        quickCopyPosition: "bottom",
    };

    constructor(props: ITableProps) {
        super(props);
        this.dataGrid = createRef();
        const storageCols = localStorage.getItem(`dt-state-${ props.name }.hCols`);

        // @ts-ignore
        const _storageCols = get(JSON.parse(storageCols), ['visible_columns']);
        this.state = {
            entities: [],
            total: 0,
            page: !isEmpty(get(props, ['moduleState', 'page'])) ? props.moduleState.page : 1,
            per_page: !isEmpty(get(props, ['moduleState', 'per_page'])) ? props.moduleState.per_page : 20,
            visible_columns: !isEmpty(_storageCols) ? intersection(props.columns.map(({field}: any) => field), _storageCols) : props.columns.map(({field}: any) => field),
            sort_field: !isEmpty(get(props, ['moduleState', 'sort_field'])) ? props.moduleState.sort_field : null,
            sort_order: !isEmpty(get(props, ['moduleState', 'sort_order'])) ? props.moduleState.sort_order : null,
            selected: [],
            fetching: false
        };
    }

    componentDidMount() {
        this.fetchData();
    }

    componentDidUpdate(prevProps: any) {
        if ( this.props.moduleState !== prevProps.moduleState  ) {
            this.fetchData();
        }
    }

    async fetchData() {
        if ( this.state.fetching ) {
            return true;
        }

        this.clearAllSelection()

        this.setState({
            fetching: true
        });

        const { url, moduleState, getMetaData, method = "GET" } = this.props;
        const payload = {
            ...moduleState,
            sort_order: (moduleState.sort_order == 1) ? "desc" : (moduleState.sort_order == -1 ? "asc" : null)
        };

        try {
            const response = await request({
                url: url,
                method,
                ...(method === "POST" ? { data: payload } : {
                    params: payload,
                    paramsSerializer: {
                        serialize: (params_1: any) => qs.stringify(params_1)
                    },
                }),
            });

            if ( getMetaData ) {
                getMetaData(response.data);
            }

            const { data, meta, extra } = response.data;

            this.setState({
                entities: data,
                total: ( meta ? meta.total : data.length ),
                page: ( meta ? meta.current_page : 1 ),
                per_page: ( meta ? meta.per_page : 10 ),
            });

            if ( this.props && this.props.setTotal ) {
                this.props.setTotal( meta ? meta.total : data.length )
            }

            if ( this.props && this.props.getExtra ) {
                this.props.getExtra( extra )
            }
        } catch (error) {
            this.setState({
                entities: [],
            });
            // console.log(error)
            throw new Error('Data Loading Error');
        } finally {
            this.setState({
                fetching: false
            });
        }
    }

    /**
     * Expose currently loaded entities (current page).
     * Useful for "select all" / merging selection in parent pages.
     */
    getEntities = () => {
        return this.state.entities || [];
    };

    /**
     * Programmatically set selection for the current grid view.
     * Note: DataTable selection expects row objects that exist in `value`.
     */
    setSelection = (rows: any[] = []) => {
        const filteredSelection = this.props.virtualScroll
            ? (rows?.filter((item: any) => !item?.__isSpacer) || [])
            : (rows || []);

        this.setState({
            selected: filteredSelection
        });

        if ( this.props.onRowSelection ) {
            this.props.onRowSelection(filteredSelection);
        }
    };

    onColumnToggle = (event: any) => {
        let selectedColumns = event.value;
        const { columns } = this.props;

        let orderedSelectedColumns = columns.filter((col: any) => selectedColumns.some((sCol: any) => sCol === col.field));

        localStorage.setItem(`dt-state-${ this.props.name }.hCols`, JSON.stringify({
            visible_columns: orderedSelectedColumns.map((col: any) => col.field)
        }));

        this.setState({ visible_columns: orderedSelectedColumns.map((col: any) => col.field) });
    };

    onPageChange = (event: any) => {
        const { rows, page } = event;

        this.props.setQuery({
            ...this.props.moduleState,
            page: ( page + 1 ),
            per_page: rows
        });
    };

    onSelectRow = (event: any) => {
        // Filter out spacer rows from selection only if virtual scroll is enabled
        const filteredSelection = this.props.virtualScroll
            ? (event.value?.filter((item: any) => !item?.__isSpacer) || [])
            : (event.value || []);

        this.setState({
            selected: filteredSelection
        });

        if ( this.props.onRowSelection ) {
            this.props.onRowSelection(filteredSelection);
        }
    };

    clearAllSelection = () => {
        this.setState({
            selected: []
        });

        if ( this.props.onRowSelection ) {
            this.props.onRowSelection([]);
        }
    };

    onExpandRow = (event: any) => {
        this.setState({
            expanded: event.data
        });
    };

    onSort = (event: any) => {
        this.props.setQuery({
            ...this.props.moduleState,
            sort_order: event.sortOrder,
            sort_field: event.sortField
        });
    }

    onRefresh = () => this.fetchData();

    render() {
        const { columns, name, size, detailRow, trackBy, onRowSelection, setRowClass, virtualScroll, state, stickyHeader, quickCopyPosition } = this.props;
        const { entities, total, page, per_page, selected, expanded } = this.state;

        if (!onRowSelection && selected.length > 0) {
            // @ts-ignore
            this.state.selected = [];
        }

        // Add a spacer row at the end only if virtual scroll is enabled
        let entitiesWithSpacer = entities;
        if (virtualScroll && entities.length > 0) {
            entitiesWithSpacer = [...entities];
            const spacerRow: any = {};
            columns.forEach((col: any) => {
                spacerRow[col.field] = '';
            });
            spacerRow[trackBy] = '__spacer__';
            spacerRow.__isSpacer = true;
            entitiesWithSpacer.push(spacerRow);
        }

        return (
            <>
                <div
                    data-table-name={ name }
                    style={
                        {
                            position: 'relative',
                            ...( stickyHeader ? { height: "800px", overflow: "hidden" } : ( virtualScroll ? {} : { height: "800px", overflowY: "scroll" }) )
                        }
                    }>
                    { this.props.showQuickCopy && name && quickCopyPosition === "top" && (
                        <div className="d-flex justify-content-end mb-2">
                            <QuickCopyDropdown
                                columns={ this.props.columns }
                                tableName={ name }
                                selectedRows={ this.props.selectedRows || [] }
                                trackBy={ trackBy }
                                entities={ entities }
                            />
                        </div>
                    ) }
                    {
                        this.props.showLoader && this.state.fetching &&
                        <div
                            style={ {
                                position: 'absolute',
                                inset: 0,
                                zIndex: 2,
                                background: 'rgba(255,255,255,0.65)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                gap: '0.5rem',
                            } }
                        >
                            <div
                                className="spinner-border text-primary"
                                role="status"
                                aria-label={ this.props.loaderLabel ?? 'Loading' }
                            />
                            <div className="text-muted small">
                                { this.props.loaderLabel ?? 'Loading' }
                            </div>
                        </div>
                    }
                    <DataTable
                        dataKey={ trackBy }
                        ref={ this.dataGrid }
                        totalRecords={ total }
                        value={ entitiesWithSpacer }
                        size={ size }
                        { ...(stickyHeader ? {
                            scrollable: true,
                            scrollHeight: "800px",
                        } : {}) }
                        { ...(!stickyHeader && !virtualScroll ? {
                            height: "800px"
                        } : {}) }
                        { ...(virtualScroll ? {
                            virtualScrollerOptions: {
                                itemSize: this.props.itemSize ?? 76,
                            },
                            scrollHeight: "800px",
                            scrollable: true,
                        } : {}) }
                        stripedRows
                        resizableColumns
                        showGridlines
                        reorderableColumns
                        { ...(state ? {
                            stateStorage: "local",
                            stateKey: `dt-state-${ name }`,
                        } : {}) }
                        selectionMode={ onRowSelection ? "multiple" : null }
                        onSelectionChange={ this.onSelectRow }
                        selection={ selected }
                        expandedRows={ expanded }
                        onRowToggle={ this.onExpandRow }
                        rowExpansionTemplate={ detailRow }
                        rowClassName={ setRowClass ?? "" }
                        onSort={ this.onSort }
                        sortMode={"single"}
                        removableSort
                        sortField={ this.props.moduleState.sort_field }
                        sortOrder={ this.props.moduleState.sort_order }
                    >
                        { onRowSelection && <Column selectionMode="multiple" headerStyle={ { width: '3rem' } } /> }
                        { detailRow && <Column
                          header={ '#' } alignHeader={ 'center' } expander={ true } style={ { width: '50px' } }
                          bodyClassName={"detailed-row"}
                        /> }

                        {
                            columns
                            && columns
                            .filter((column: any) => this.state.visible_columns.some((vc: any) => vc === column.field))
                            .map((item: any, index: number) => {
                                return (
                                    <Column
                                        // @ts-ignore
                                        alignHeader={ item.align ?? 'left' }
                                        bodyStyle={ { textAlign: item.align ?? 'left', backgroundColor: item.bgColor ?? "initial" } }
                                        field={ item.field } header={ item.title } body={ (rowData: any) => {
                                            if (virtualScroll && rowData?.__isSpacer) {
                                                return <div style={{ height: '220px', visibility: 'hidden' }}></div>;
                                            }
                                            return item.render ? item.render(rowData) : rowData[item.field];
                                        } }
                                        hidden={ item.hidden ?? false } key={ item.field }
                                        sortable={ item.sorting === true }
                                        sortField={ item.sortField ?? item.field }
                                        bodyClassName={ cx(css`
                                          &:before {
                                            content: ${ item.title } ': ';
                                          }
                                        `, `${ name }-${ item.title } ${ item.title }`)
                                        }
                                    />
                                );
                            })
                        }
                    </DataTable>
                </div>
                {/* Custom Footer Outside Scrollable Area */}
                <div style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    backgroundColor: 'var(--surface-ground)',
                    borderRadius: '4px',
                    borderTop: '1px solid var(--surface-border)'
                }}>
                    <Row>
                        <Col sm={ 12 } md={ 6 } className={ "sm:mt-2" }>
                            <Paginator
                                first={ ( page - 1 ) * per_page }
                                rows={ per_page }
                                totalRecords={ total }
                                pageLinkSize={ 3 }
                                rowsPerPageOptions={ [10, 20, 50, 200, 500] }
                                onPageChange={ this.onPageChange }
                                alwaysShow
                            />
                        </Col>
                        <Col sm={ 12 } md={ 3 } className={ "mt-3" }>
                            <MultiSelect
                                options={ this.props.columns.map((column: any) => ({id: column.field, label: column.title})) }
                                onChange={ this.onColumnToggle }
                                value={ this.state.visible_columns }
                                optionValue={ "id" }
                                filter
                                optionLabel={ "label" }
                                className="w-full sm:w-20rem"
                                display="chip"
                            />
                        </Col>
                        <Col sm={ 12 } md={ 3 } className={ "mt-3" }>
                            { this.props.showQuickCopy && name && quickCopyPosition !== "top" && (
                                <QuickCopyDropdown
                                    columns={ this.props.columns }
                                    tableName={ name }
                                    selectedRows={ this.props.selectedRows || [] }
                                    trackBy={ trackBy }
                                    entities={ entities }
                                />
                            )}
                        </Col>
                    </Row>
                </div>
            </>
        );
    }
}

export default StateLessGrid;
